/**
 * How firm the staffing need is — the Solution's open Staffing Request priority, mirrored
 * live onto the plan (RP-18.4). Read-only: it is changed on the request, never here.
 *
 * Coloured by the priority's RANK among the active priorities (0 = most urgent), not by its
 * name or sort number, because admins can rename and renumber priorities.
 */

import { cn } from "@/lib/utils";
import type { PlanStaffingNeedView } from "@/schemas/resource-plans";

function rankClass(rank: number): string {
  if (rank === 0) return "bg-error-100 text-error-700";
  if (rank === 1) return "bg-warning-100 text-warning-700";
  return "bg-info-100 text-info-700";
}

export function staffingNeedLabel(need: PlanStaffingNeedView): string {
  return need.priority?.name ?? "No priority";
}

export function StaffingNeedBadge({
  need,
  showCount = false,
}: {
  need: PlanStaffingNeedView | null | undefined;
  showCount?: boolean;
}) {
  if (!need) return <span className="text-sm text-muted-foreground">—</span>;
  const count = need.openRequestCount;
  const tooltip = `${count} open Staffing Request${count === 1 ? "" : "s"}${
    need.priority ? ` — most urgent: ${need.priority.name}` : " — none has a priority"
  }. Set on the request, not on the plan.`;
  return (
    <span
      title={tooltip}
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-sm font-medium",
        need.priority ? rankClass(need.priority.rank) : "bg-muted text-muted-foreground",
      )}
    >
      {staffingNeedLabel(need)}
      {showCount && <span className="ml-1 font-normal">· {count} open</span>}
    </span>
  );
}
